/**
 * お金の画面の小さな判断（2026-09-27 money/dashboard-3ds-self）: 前払いの入金の結果・自分の支払い（売上に含めない）の見せ方。
 *
 * WHY moneyLogic.ts に足さないか: moneyLogic.ts は 400 行を超えている（300 行の目安）。今回の 2 つは pages/money の画面だけが使うので、
 * 画面の隣に置く。moneyLogic.ts と同じく import を持たない = `node --test`（型の除去だけ）で動く（moneyDisplay.test.ts）。
 * 文言はここに書かない（返すのは状態の名前。画面が i18n のキーに引き直す）。
 */

// ---------------------------------------------------------------- 前払いの入金の結果

/** サーバーの failure_reason（apps/server/src/modules/payments/protection.ts CARD_NOT_PROTECTED・migration 0251） */
export const CARD_NOT_PROTECTED = 'card_not_protected';

/**
 * 入金（GET /v1/money/prepaid/checkouts/:id・POST …/checkout の応答）の状態を、画面が言うこと 1 つにまとめる。
 *   done          … 残高に入った（status = completed のときだけ）
 *   checking      … まだ決まっていない（pending）。「確認しています」
 *   card_refused  … このカードでは入金できない（3D セキュアで守られないカードを断った）→ 別のカードを促す
 *   failed        … それ以外の失敗・期限切れ（請求はされていない）
 *
 * WHY completed のときだけ done か: 3ds-refuse（2026-09-27）から入金は手動キャプチャ = Checkout から戻った時点では
 * 「オーソリ済み・判定前」で、そのあと断られる（オーソリ取り消し）ことがある。戻った・ポップアップが閉じた = 完了、と扱うと
 * 一瞬「入金しました」と出てから断られる。状態が completed になるまでは checking のまま。
 *
 * WHY 断った理由（3D セキュア・不正対策）を画面に書かないか: 断る条件を不正をする側に教えない（founder 判断 2026-09-27）。
 * 本物の利用者に必要なのは「このカードはだめ・別のカードで」だけ。
 */
export type TopUpOutcome = 'done' | 'checking' | 'card_refused' | 'failed';

export function topUpOutcome(t: { status: string; failureReason?: string | null }): TopUpOutcome {
  if (t.status === 'completed') return 'done';
  if (t.status === 'pending') return 'checking';
  if (t.status === 'failed' && t.failureReason === CARD_NOT_PROTECTED) return 'card_refused';
  // expired・failed（authorization_canceled 等）・未知の状態: 入金されていない側に倒す（「入金しました」と誤って言わない）
  return 'failed';
}

/**
 * Checkout の「戻る（キャンセル）」から戻ったか。戻り先の URL にこの印を付けておく（PrepaidPage の TopUpCard）。
 * WHY: サーバーは成功・キャンセルのどちらの戻り先にも `?kumo_topup=<id>` を付ける（prepaidTopup.ts withTopUpParam）。
 * キャンセルでは入金は pending のまま（期限切れまで）なので、印が無いと 20 秒「確認しています」を出し続けてしまう。
 */
export const TOPUP_CANCELED_PARAM = 'kumo_topup_canceled';

// ---------------------------------------------------------------- 自分の支払い（売上に含めない）

/**
 * GET /v1/money/self-purchases の状態（apps/server/src/modules/money/selfPurchase.ts・0252）。
 *   excluded … 保留中（売上に含めず、出金できない勘定にある。期日に前払いへ移す）
 *   prepaid  … 前払いへ移した（利用料に使える・出金はできない）
 *   released … 運営が解除した（誤判定 = 通常の売上へ戻した）
 */
export type SelfPurchaseStatus = 'excluded' | 'prepaid' | 'released';
const SELF_STATUSES: readonly string[] = ['excluded', 'prepaid', 'released'];

/** 画面の i18n キーの末尾。未知の状態（サーバーが足した）は null = 画面はコードをそのまま出す（壊れない） */
export function selfPurchaseStatusKey(status: string): SelfPurchaseStatus | null {
  return SELF_STATUSES.includes(status) ? (status as SelfPurchaseStatus) : null;
}

/**
 * 一覧の 1 行で見せる額: いまの状態で「その購入がどれだけ動いたか」。
 *   excluded → いま出金できない勘定にある額（heldNow。一部返金の後は減る）
 *   prepaid  → 前払いへ移した額
 *   released → 売上へ戻した額（古い行で null なら総額）
 * WHY 総額（gross）だけにしないか: 総額は払った額で、決済手数料・返金の後に動いた額と違う。状態の列と並べて読める額にする
 */
export function selfPurchaseAmount(r: { status: string; gross: number; heldNow: number; movedToPrepaid: number; releasedAmount: number | null }): number {
  if (r.status === 'excluded') return r.heldNow;
  if (r.status === 'prepaid') return r.movedToPrepaid;
  if (r.status === 'released') return r.releasedAmount ?? r.gross;
  return r.gross;
}

/**
 * 売上の「収入源別」の表に載せる行。自分の支払い（source = self_purchase）は除く。
 * WHY: 売上に含めない額を売上の表に混ぜると「売上として数えられた」ように読める。自分の支払いは別のカードで説明つきで見せる
 */
export function earningSources<T extends { source: string }>(rows: T[]): T[] {
  return rows.filter((r) => r.source !== 'self_purchase');
}

/**
 * 自分の支払いのカードを出すか。0 のとき（出金できない勘定が 0・一覧も空）は出さない（関係の無い人に説明を見せない）。
 * 一覧だけある（すべて前払いへ移した・解除した）ときも出す = 「売上が消えた」と思わせないため、どこへ行ったかを見せる
 */
export function showSelfPurchases(balance: number | undefined, listed: number, byGame: Array<{ selfPurchases?: number }> = []): boolean {
  return (balance ?? 0) > 0 || listed > 0 || byGame.some((g) => (g.selfPurchases ?? 0) > 0);
}

// ---------------------------------------------------------------- 前払いの画面（Billing）の一番上の知らせ

/**
 * 前払いの画面（PrepaidPage）の残高の札のすぐ下に出す知らせを 1 つ選ぶ（2026-11-05 money/zeroband・docs/teams/money/zeroband/findings.md）。
 *   owed … 赤「止まっている（請求残 {owed}）」pp.owedTitle
 *   out  … 赤「使い切った」pp.outTitle（招待クレジットを使い切った人は pp.outTitleInvite）
 *   low  … 黄「残高が少なくなっています」pp.lowTitle（招待クレジットの残りがわずかなら pp.lowTitleInvite）
 *   null … 何も出さない
 * 3 つは同時に出ない（以前の画面の式と同じ順: owed → out → low）。
 *
 * WHY 'start'（一度も入れていない $0 の新しい人 = prepaidGate.ts prepaidBand）に low を出さないか:
 *   10/31 から招待なしで登録でき、その人は $0 から始まる。サーバーは $0 の人に balanceStatus 'out' を返し、moneyLogic.ts prepaidTone は
 *   'out' を 'low' にまとめるので、以前は入れたことのない人にも「Your balance is running low … so nothing pauses」が出ていた。
 *   一度も入れていない人に「少なくなった」と言うのは嘘。サーバーの 402 の文も同じ理由で Core ３ が直した
 *   （ブランチ core/open-signup・コミット 6e0f5c123・servingGate.ts assertCanSpend）。
 *   その人の「次にすること」（入金）は、すぐ下の入金の欄（金額の候補と「支払う」のボタン）がそのまま示すので、代わりの文は足さない
 *   （画面で確かめた: docs/teams/money/zeroband/screens/）。'start' には赤の「使い切った」も出さない（以前から: ranOut の式）。
 * WHY 招待の確認待ち（'invitePending'）にも low を出さないか（2026-11-05 money/zerotone・docs/teams/money/zerotone/findings.md）:
 *   招待コードを押さえてメールの確認を待っている人も $0・balanceStatus 'out' なので、以前は同じ黄色い帯
 *   「Top up, or turn on automatic top-up」が出ていた。だが確認前は入金も 403 email_unverified = 言われた一手が押せない。
 *   その人の次の一手は「メールを確認する（すると招待の額が入る）」で、それは同じ画面の InviteCodeCard（MoneyShell）の
 *   invite.card.pending と、上の帯（PrepaidBand）が言う（写真 docs/teams/money/zerotone/screens/）。prepaidGate.ts billingLowAlert
 *   （Core ３ core/first5 f25582ac7）も同じ判断。zeroband（8d26eab85）の時は「招待の人の動きは変えない」の指示で残していた。
 * 入れたことのある人が、ちょうど $0 まで使い切ったときも 'start' に見える（prepaidGate.ts notFundedYet の限界）。その人にも帯は出ないが、
 *   残高の札は $0 で黄色・入金の欄はそのまま = 次にすることは分かる（「少ない」と言うより誤りが無い）。
 */
export type PrepaidPageAlert = 'owed' | 'out' | 'low' | null;

export function prepaidPageAlert(x: {
  /** moneyLogic.ts prepaidTone の結果 */
  tone: string | null;
  /** prepaidGate.ts prepaidBand の結果 */
  band: string | null;
  /** 解決していない low_balance の知らせがある */
  lowNotice: boolean;
  /** 解決していない balance_due の知らせがある */
  dueNotice: boolean;
}): PrepaidPageAlert {
  if (x.tone === 'owed') return 'owed';
  // 使い切った（請求残なし・止まっている）: お金以外の画面の帯と同じ判定（'empty'）か、止まったときの知らせ（balance_due）が解決していない。
  //   新しい人（start）・招待の確認待ちには「使い切った」と言わない
  if (x.band === 'empty' || (x.dueNotice && x.band !== 'start' && x.band !== 'invitePending')) return 'out';
  // 一度も入れていない人（start）・確認待ち（invitePending）に「少なくなった・入金して」と言わない（上の WHY）
  if (x.band === 'start' || x.band === 'invitePending') return null;
  if (x.tone === 'low' || x.lowNotice) return 'low';
  return null;
}

// ---------------------------------------------------------------- 前払いの画面（Billing）の残高の札 2 つの色

/**
 * 残高の札（pp.balance「Balance」）ともつ日数の札（pp.runway「Lasts about」）の色（2026-11-05 money/zerotone・
 * docs/teams/money/zerotone/findings.md・prepaid-zero-band の続き = zeroband findings の「残り」2）。
 *   balance: 'accent'（左に印の線・ふだん）/ 'warn'（数字が警告の色 = 黄土色）
 *   runway:  'warn' / undefined（色なし）
 *
 * WHY 'start'（一度も入れていない $0 の新しい人）を警告の色にしないか:
 *   10/31 から招待なしで登録した人は全員 $0 で始まる。サーバーは $0 に balanceStatus 'out' を返し、prepaidTone はそれを 'low' にまとめるので、
 *   以前は「Balance $0.00」「Lasts about: No usage yet」の 2 枚が警告の色だった。帯（prepaidPageAlert）を消しても（8d26eab85）
 *   札が黄色いままだと「何か悪くなった」と読める。入れたことのない人には悪くなった物が無い = ふだんの色（残高は accent）。
 *   次にすること（入金）はすぐ下の入金の欄が示す。
 *   Core ３の案（core/first5 の知らせ）`(tone==='low'||tone==='owed') && band!=='start'` をそのまま runway に使い、残高の札にも同じ除外を当てた。
 * 'start' に請求残は無い（prepaidBand は owed > 0 なら 'owed'）ので、除外で請求残の警告が消えることは無い。
 * 入れた額をちょうど $0 まで使い切った人も 'start' に見える（prepaidGate.ts notFundedYet の限界）。その人の札もふだんの色になるが、
 *   残高 $0.00 の数字と入金の欄は見える（帯と同じく、穏やかな方に倒す）。
 * WHY 招待の確認待ち（'invitePending'）も警告の色にしないか（2026-11-05 money/pendingpay・docs/teams/money/pendingpay/findings.md）:
 *   その人もまだ 1 度もお金が入っていない $0（確かめると招待の額が入る）= 'start' と同じく悪くなった物が無い。
 *   zerotone（5cd4d6f96）では指示の範囲が 'start' だけだったので黄土色のまま残していた（zerotone findings の「残り」1）。
 *   'invitePending' は canSpend true の人には付かず（prepaidGate.ts）、請求残の札（pp.owed）は別の札で tone="warn" 固定なので消えない。
 * 文言はここに無い（色の名前だけ = 画面が StatTile の tone に渡す）。
 */
export function prepaidTileTones(x: {
  /** moneyLogic.ts prepaidTone の結果 */
  tone: string | null;
  /** prepaidGate.ts prepaidBand の結果 */
  band: string | null;
}): { balance: 'accent' | 'warn'; runway: 'warn' | undefined } {
  // 一度もお金が入っていない人（start = 入金していない・invitePending = 招待の額がまだ入っていない）
  const neverFunded = x.band === 'start' || x.band === 'invitePending';
  // 以前の画面の式（PrepaidPage.tsx に直に書いていた）: balance = ok/idle なら accent、それ以外 warn / runway = low/owed なら warn
  const balanceWarn = !(x.tone === 'ok' || x.tone === 'idle') && !neverFunded;
  const runwayWarn = (x.tone === 'low' || x.tone === 'owed') && !neverFunded;
  return { balance: balanceWarn ? 'warn' : 'accent', runway: runwayWarn ? 'warn' : undefined };
}

// ---------------------------------------------------------------- 前払いの画面（Billing）の入金・自動チャージ ON を押せる形にするか

/**
 * 入金（「Pay $26.66」・「Save this card」）と自動チャージの ON を押せない形にするか（2026-11-05 money/pendingpay・
 * docs/teams/money/pendingpay/findings.md・zerotone findings の「残り」2）。true = 押せない形 + すぐ近くに「メールを確かめると…」の 1 行。
 *
 * WHY: サーバーはメール未確認の人の入金（POST /v1/money/prepaid/checkout 全体 = カードの保存も）と自動チャージの ON
 *   （PUT /v1/money/prepaid/settings の autoTopUp.enabled = true）を 403 email_unverified で断る（apps/server/src/modules/money/emailVerified.ts・
 *   管理の決定 2026-09-28 = 先回りの登録をした人に盗んだカードで入金させない）。以前の画面はボタンを押せる形で出していて、
 *   押すと赤い「先にメールアドレスを確かめてください」が出る = 押せるのに必ず失敗するボタンだった。ここではサーバーの決まりは変えず、
 *   画面を先にそろえるだけ（サーバーの 403 は最後の守りのまま残る）。
 * WHY band 'invitePending' だけでなく emailVerified も見るか: 403 の条件は「メール未確認」で、招待コードの有無ではない。
 *   10/31 から招待なしでも登録でき、その人がメールを確かめる前は band 'start' のまま同じ 403 になる（PrepaidBand.tsx の startUnverified と同じ人）。
 *   'invitePending'（invite.pending）はメール確認の前にしか付かない = セッションの emailVerified が古い・無いときの補い。
 * emailVerified が undefined（古いサーバー・分からない）なら推測で止めない（前と同じ = 押せる。押せばサーバーが言う）。
 * 「残高の線（pp.lowLine）」の保存は確認前でもできる（サーバーは autoTopUp.enabled = true のときだけ断る）ので、設定の「保存」は止めない。
 */
export function prepaidFundingLocked(x: {
  /** prepaidGate.ts prepaidBand の結果 */
  band: string | null;
  /** セッションの developer.emailVerified（api.ts Developer・古いサーバーは undefined） */
  emailVerified: boolean | undefined;
}): boolean {
  return x.emailVerified === false || x.band === 'invitePending';
}
